import React, { useEffect, useMemo, useState } from 'react';
import { Download, Search, UserPlus, X, ArrowRight } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Card';
import { Input, Field } from '@/components/ui/Input';
import PhoneField, { isValidPhoneNumber } from '@/components/ui/PhoneField';
import { useToast } from '@/components/ui/Toast';
import { formatDate } from '@/lib/utils';

export default function AdminCustomers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/customers');
      setRows(data);
    } catch {
      toast({ title: 'Failed to load users', variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((c) => (c.name + ' ' + c.email + ' ' + (c.phone || '')).toLowerCase().includes(s));
  }, [q, rows]);

  const exportCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Joined', 'Member ID', 'Active Plan', 'Bookings'];
    const lines = filtered.map((c) => [
      c.name, c.email, c.phone || '—', formatDate(c.created_at),
      c.active_membership?.member_code || '—',
      c.active_membership?.plan_name || '—',
      c.bookings_count,
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const blob = new Blob([[headers.join(','), ...lines].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `kba-users-${Date.now()}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="admin-customers">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-volt mb-2">Audit</div>
          <h1 className="font-heading font-black text-4xl uppercase tracking-tight">Users</h1>
          <p className="text-sm text-white/50 mt-2 max-w-xl">Every registered user. Members (with active paid plans) appear in the separate "Members" tab.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCSV}><Download size={14} /> Export CSV</Button>
          <Button onClick={() => setShowAdd(true)} data-testid="add-customer-button"><UserPlus size={14} /> Add User</Button>
        </div>
      </div>

      <div className="bg-surface border border-white/10 p-5">
        <Field label="Search">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="name, email, phone…" className="pl-10" data-testid="users-search" />
          </div>
        </Field>
      </div>

      <div className="bg-surface border border-white/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="border-b border-white/10 bg-obsidian/40">
            <tr className="text-[10px] uppercase tracking-[0.25em] text-white/40">
              <th className="text-left px-5 py-3">Name</th>
              <th className="text-left px-5 py-3">Contact</th>
              <th className="text-left px-5 py-3">Joined</th>
              <th className="text-left px-5 py-3">Member Status</th>
              <th className="text-right px-5 py-3">Bookings</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="5" className="px-5 py-12 text-center text-white/40">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan="5" className="px-5 py-12 text-center text-white/40">No users yet.</td></tr>
            ) : filtered.map((c) => (
              <tr key={c.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`user-row-${c.id}`}>
                <td className="px-5 py-3">
                  <div className="font-medium">{c.name}</div>
                  <div className="text-[10px] text-white/40 uppercase tracking-wider">{c.id.slice(0, 8)}</div>
                </td>
                <td className="px-5 py-3 text-white/70 text-xs">
                  <div>{c.email}</div>
                  <div className="text-white/40 font-mono">{c.phone || '—'}</div>
                </td>
                <td className="px-5 py-3 text-white/60">{formatDate(c.created_at)}</td>
                <td className="px-5 py-3">
                  {c.active_membership ? (
                    <div>
                      <Badge tone="success">{c.active_membership.member_code} · {c.active_membership.plan_name}</Badge>
                      <div className="text-[10px] text-white/40 mt-1">till {formatDate(c.active_membership.expires_at)}</div>
                    </div>
                  ) : <Badge>Not a member</Badge>}
                </td>
                <td className="px-5 py-3 text-right font-mono">{c.bookings_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <AddCustomerModal onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); load(); }} />
      )}
    </div>
  );
}

function AddCustomerModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [busy, setBusy] = useState(false);
  const [tempPw, setTempPw] = useState(null);
  const [error, setError] = useState('');
  const { toast } = useToast();

  const save = async () => {
    setError('');
    if (!form.name || !form.email || !form.phone) return setError('All fields are required');
    if (!isValidPhoneNumber(form.phone)) return setError('Invalid phone number');
    setBusy(true);
    try {
      const { data } = await api.post('/admin/customers/quick-create', form);
      if (data.existed) {
        toast({ title: 'User already exists', description: data.email, variant: 'default' });
        onCreated();
      } else {
        setTempPw(data.temporary_password);
        toast({ title: 'User created', description: data.email, variant: 'success' });
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Create failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-surface border border-white/10 max-w-md w-full p-6 sm:p-8" onClick={(e) => e.stopPropagation()} data-testid="add-customer-modal">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading font-bold text-2xl uppercase">Add User</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X size={18} /></button>
        </div>
        {tempPw ? (
          <div className="space-y-4">
            <p className="text-sm text-white/70">User created successfully. Share this temporary password with the customer — they can change it later via "Forgot Password".</p>
            <div className="bg-volt/10 border border-volt p-4 font-mono text-volt text-lg text-center select-all">{tempPw}</div>
            <Button onClick={onCreated} className="w-full">Done <ArrowRight size={14} /></Button>
          </div>
        ) : (
          <div className="space-y-4">
            <Field label="Full Name" error={error}>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Karthik R." data-testid="new-user-name" />
            </Field>
            <Field label="Email">
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" data-testid="new-user-email" />
            </Field>
            <Field label="Phone (pick country + number)">
              <PhoneField value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} defaultCountry="IN" testId="new-user-phone" />
            </Field>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
              <Button onClick={save} disabled={busy} className="flex-1" data-testid="save-new-user">{busy ? 'Creating…' : 'Create User'}</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
